import type { CompanyAssessmentSummary } from "../api/usGrowth";

export const dimensionLabels: Record<string, string> = {
  identity: "Identité de la société",
  screening: "Exclusions commerciales",
  funding: "Dernier financement",
  funding_amount: "Montant du tour et cohorte",
  finance_team_level: "Équipe finance",
  has_hr_lead: "Responsable RH",
  has_cos_coo: "COO / Chief of Staff",
  hiring_finance_level: "Recrutement finance",
  hiring_stratops: "Recrutement opérations",
  hiring_hr: "Recrutement RH",
  founder_geography: "Parcours des founders",
  is_excluded_incubator: "Incubateurs / accélérateurs",
  is_blacklisted_sector: "Secteur d’activité",
  growth: "Croissance des effectifs",
};
export const qualityLabels: Record<string, string> = {
  COMPLETE: "Documenté",
  PARTIAL: "Incomplet",
  MISSING: "À rechercher",
  STALE: "À actualiser",
  CONTRADICTED: "Contradiction",
  SOURCE_UNAVAILABLE: "Source indisponible",
};
export const decisionLabels: Record<string, string> = {
  QUALIFIED: "Qualifiée CFO",
  NOT_QUALIFIED: "Hors critères CFO",
  NEEDS_DATA: "Données à compléter",
  NEEDS_REVIEW: "Revue nécessaire",
  SUPPRESSED: "Exclusion commerciale",
};
export function assessmentScore(a: CompanyAssessmentSummary | null): string {
  if (!a) return "À évaluer";
  if (a.score === null) return "Non applicable";
  return `${a.score}${a.provisional || !a.current ? " · provisoire" : ""}`;
}
export function assessmentNext(a: CompanyAssessmentSummary | null): string {
  if (!a) return "Évaluation nécessaire";
  if (!a.current) return "Actualiser l’évaluation";
  if (a.decision === "SUPPRESSED" || a.decision === "NOT_QUALIFIED")
    return "Consulter le motif";
  if (a.next_dimension)
    return `À documenter : ${dimensionLabels[a.next_dimension] ?? a.next_dimension}`;
  return "Données documentées";
}
export function dimensionReason(reason: string): string {
  const labels: Record<string, string> = {
    OBSERVATION_MISSING: "Aucune preuve admissible conservée.",
    IDENTITY_SOURCE_CONFLICT:
      "Les sources renvoient à des identités de société incompatibles. Rapprochez les preuves avant de conclure.",
    ABSENCE_NOT_ESTABLISHED_IN_SOURCE:
      "La recherche est partielle : une absence ne peut pas être conclue.",
    STALE_OR_FUTURE:
      "La date de cette observation ne permet plus de l’utiliser.",
    TECHNICAL_FAILURE: "La source n’a pas fourni de réponse exploitable.",
    REFERENCE_ROUND_USD_AMOUNT_UNKNOWN:
      "Le montant du tour en USD reste à établir, sans utiliser le financement cumulé.",
    HISTORICAL_COHORT_ADMISSION_POLICY_REQUIRED:
      "La cohorte autorisée couvre les sociétés admises au workflow financement. Le périmètre historique reste à arbitrer ; aucun quartile n’est inventé.",
    DOCUMENTED_PATH_BOUNDED:
      "Un parcours est documenté ; les preuves disponibles ne fixent pas encore tous les points.",
    REQUIRED_EVIDENCE_INCOMPLETE:
      "Les preuves ne couvrent pas encore l’ensemble du critère.",
    VERIFIED_POSITIVE_OR_VALUE: "Une preuve exploitable est conservée.",
    DOCUMENTED_FOUNDER_PATH: "Parcours documenté selon la règle CFO du projet.",
  };
  return (
    labels[reason] ??
    "La preuve ou sa couverture doit être examinée avant de conclure."
  );
}

export function assessmentReason(reason: string): string {
  const labels: Record<string, string> = {
    CURRENT_SUPPRESSION: "Exclusion commerciale confirmée.",
    REQUIRED_OBSERVATIONS_MISSING_OR_STALE:
      "Des preuves obligatoires manquent ou sont trop anciennes.",
    OPTIONAL_DIMENSIONS_DECISIVE:
      "Les critères encore inconnus peuvent changer la décision.",
    ASSESSMENT_RECALCULATION_REQUIRED:
      "Le diagnostic doit être recalculé avec les données actuelles.",
    CERTAIN_SCORE_AT_THRESHOLD: "Le score démontré atteint le seuil CFO.",
    MAXIMUM_SCORE_BELOW_THRESHOLD:
      "Même le score maximal possible reste sous le seuil CFO.",
    MISSING_DATA_NOT_DECISIVE:
      "Les données restantes ne peuvent pas changer cette décision.",
    HISTORICAL_COMPANY_BLOCK_REQUIRES_RECONCILIATION:
      "Un blocage historique de la société doit être examiné.",
    HUMAN_CORRECTION_REQUIRES_SOURCE_RECONCILIATION:
      "Une correction humaine doit être rapprochée de ses preuves.",
    PUBLIC_HOMONYM_HAS_DIFFERENT_FOUNDER_ADARGA:
      "Le site public d’Adarga décrit un autre fondateur que le dossier historique.",
    PUBLIC_HOMONYM_HAS_DIFFERENT_FOUNDER_BRAINFISH:
      "Le site public de Brainfish décrit d’autres fondateurs que le dossier historique.",
    HISTORICAL_GANGKHAR_NONPROFIT_DIFFERS_FROM_NEW_INSURTECH:
      "L’organisme Gangkhar historique et la nouvelle insurtech semblent distincts ; leur rapprochement reste à établir.",
    "CFO already in place": "Un CFO est déjà en poste.",
    "Post Series A": "Le financement dépasse le stade Series A autorisé.",
    "Post Series A (provider stage)": "Le stade fournisseur dépasse Series A.",
    "Excluded incubator": "Un incubateur exclu est documenté.",
    "Blacklisted sector": "Le secteur figure dans les exclusions CFO.",
    "No money raised":
      "L’absence de financement établie est hors critères CFO.",
  };
  return labels[reason] ?? reason;
}
